/**
 * Inline activity labels (MapActivityLabel.vue): a state-coloured rounded pill
 * with the left state glyph (projects only — like the SVG `UiStateIcon`), the
 * activity title ellipsis-truncated to the in-lane next activity, and the
 * favourite star. A banner showing for an activity suppresses its label (the
 * unified decision lives in the content frame).
 *
 * Geometry mirrors the SVG: the pill is vertically centred on `getActivityY`
 * (rect top at `getActivityY − 10`, height 20); the title starts 17px in (after
 * the 15px state glyph) for projects, or at the left padding for templates.
 */
import { drawIcon, stateIconColor, stateIconName } from '../../draw/icons';
import { cssVar, labelColors, modeOf } from '../../draw/palette';
import { roundRect } from '../../draw/shapes';
import { truncate } from '../../draw/text';
import { withGroupOpacity } from '../../engine/groupOpacity';
import { layoutY } from '../../layout/activityLayout';
import { isSelectedActivity } from './selection';
import type { SceneContext } from '../../engine/SceneContext';
import type { MapActivity } from '@/store/ioStore';

// Local x of the title: after the 15px state glyph (projects), or flush (templates).
const TITLE_OFFSET = 17;
// Local x of the pill's left edge relative to the activity-label origin.
const PILL_LEFT = -10;

const LABEL_FONT = '11px Roboto, sans-serif';

/** A measured, ready-to-paint label (geometry resolved on the main ctx). */
interface LabelSpec {
  activity: MapActivity;
  x: number;
  cy: number;
  titleX: number;
  text: string;
  width: number;
  fav: boolean;
  isTemplate: boolean;
}

export function drawLabels(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map } = scene;
  const visible = scene.content?.visible ?? [];
  const bannerIds = scene.content?.bannerIds ?? new Set<string>();

  // labelMode 'none' hides all labels, except a 'link' selection still shows
  // them (mirrors MapActivityLabel.showLabel).
  if (map.labelMode === 'none' && map.selectedContext !== 'link') {
    return;
  }

  const isTemplate = map.isTemplate;

  ctx.font = LABEL_FONT;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.lineWidth = 1;

  // Filter-shadowed labels render at 0.2 as GROUP opacity (SVG
  // `.map-activity-labels.shadowed`): the pill, state glyph, title and star must
  // flatten before compositing, else the glyph strokes / text over the pill
  // darken. Measure all labels on the main ctx (font set), draw un-dimmed ones
  // straight to the layer, and batch the dimmed ones through one group pass.
  const dimmed: LabelSpec[] = [];
  // One reusable spec for the common (un-dimmed) path — it's painted straight
  // away inside the loop and never retained, so we mutate a single object
  // instead of allocating one per label per frame. Dimmed labels are deferred
  // to the group pass below and so still need their own captured objects.
  const scratch: LabelSpec = {
    activity: null as unknown as MapActivity,
    x: 0,
    cy: 0,
    titleX: 0,
    text: '',
    width: 0,
    fav: false,
    isTemplate,
  };
  const collapsed = scene.layout?.collapsedIds;
  for (const activity of visible) {
    // Collapsed group members draw neither hexagon nor label — the group
    // marker (with the member count) stands in for them.
    if (collapsed?.has(activity.id)) {
      continue;
    }
    if (!shouldShowLabel(scene, activity, bannerIds)) {
      continue;
    }

    const baseX = scene.screenX(map.activityPos(activity));
    const x = baseX + 18;
    // Vertical centre of the pill (SVG rect spans this ±10).
    const cy = layoutY(scene, activity.id);
    const titleX = x + (isTemplate ? 0 : TITLE_OFFSET);
    const fav = !isTemplate && !activity.mirrored && !!activity.isFavorite;

    // Gap to the next activity sharing this label's row (classic: the in-lane
    // successor; flow: the next same-row neighbour from the layout): the title
    // must end 15px before it, and it starts at `titleX`.
    const limitX = scene.layout?.labelLimitX(activity.id) ?? Infinity;
    let maxWidth =
      (Number.isFinite(limitX) ? limitX : Number.MAX_SAFE_INTEGER) -
      titleX -
      15;
    if (fav) {
      maxWidth -= 18; // room for the favourite star (12px glyph + gap)
    }
    if (maxWidth < 6) {
      continue;
    }

    const title = activity.mirrored
      ? `${activity.title} || ${activity.sourceProjectTitle ?? ''}`
      : activity.title;
    const { text, width } = truncate(ctx, title, maxWidth);

    if (map.isShadowedActivityId(activity.id)) {
      // Captured for the deferred group pass — must be its own object.
      dimmed.push({ activity, x, cy, titleX, text, width, fav, isTemplate });
    } else {
      scratch.activity = activity;
      scratch.x = x;
      scratch.cy = cy;
      scratch.titleX = titleX;
      scratch.text = text;
      scratch.width = width;
      scratch.fav = fav;
      paintLabel(ctx, scratch);
    }
  }

  if (dimmed.length) {
    withGroupOpacity(ctx, 0.2, (g) => {
      g.font = LABEL_FONT;
      g.textBaseline = 'middle';
      g.textAlign = 'left';
      g.lineWidth = 1;
      for (const spec of dimmed) {
        paintLabel(g, spec);
      }
    });
  }
}

/** Paint a single inline label at full opacity (dimming is the caller's job). */
function paintLabel(ctx: CanvasRenderingContext2D, spec: LabelSpec): void {
  const { activity, x, cy, titleX, text, width, fav, isTemplate } = spec;
  const colors = labelColors(modeOf(activity.state, activity.status));

  // Rounded, state-coloured background (SVG rect: translate(-10,-14), h20, r3).
  const contentW = (isTemplate ? 0 : TITLE_OFFSET) + width + (fav ? 18 : 0);
  const bgWidth = contentW + 20;
  ctx.fillStyle = colors.fill;
  ctx.strokeStyle = colors.stroke;
  roundRect(ctx, x + PILL_LEFT, cy - 10, bgWidth, 20, 3);
  ctx.fill();
  ctx.stroke();

  // Left state glyph (projects only, glyph-only like UiStateIcon), centred.
  if (!isTemplate) {
    drawIcon(
      ctx,
      stateIconName(activity.state, activity.status),
      x + 7,
      cy,
      15,
      stateIconColor(activity.state, activity.status),
    );
  }

  // Title, vertically centred in the pill.
  ctx.fillStyle = colors.text;
  ctx.fillText(text, titleX, cy + 1);

  // Favourite star, just past the title.
  if (fav) {
    drawIcon(
      ctx,
      'Star',
      titleX + width + 4 + 6,
      cy,
      12,
      cssVar('--color-warning-400', '#FDB022'),
      true,
    );
  }
}

/** MapActivityLabel.showLabel ported: whether an inline label should draw. */
function shouldShowLabel(
  scene: SceneContext,
  activity: MapActivity,
  bannerIds: Set<string>,
): boolean {
  const { map } = scene;
  // A banner showing for this activity suppresses its inline label (this is the
  // unified label-vs-banner decision computed once in the content frame).
  if (bannerIds.has(activity.id)) {
    return false;
  }
  if (map.selectedContext === 'link') {
    return true;
  }
  if (map.hoveredActivity === activity.id) {
    return false;
  }
  if (isSelectedActivity(scene, activity)) {
    return false;
  }
  if (map.labelMode === 'none') {
    return false;
  }

  return true;
}
